/**
 * PrivacyAccountDeletionModal — danger zone only (no policy list).
 * Same BaseModal + sheet pattern as PrivacyLegalDocumentModal / World Cup rules.
 *
 * Confirmation is INLINE (type-to-confirm) inside this sheet. We must NOT open a
 * secondary global Dialog here: on iOS this sheet is already a stacked native
 * Modal, and presenting another Modal from the busy root view controller fails
 * silently (the dialog never appears), leaving the user stuck on this screen.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  View,
  Text,
  ScrollView,
  Pressable,
  TextInput,
  Platform,
  ActivityIndicator,
  useWindowDimensions,
} from 'react-native'
import { X, Clock } from 'lucide-react-native'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations'
import { useTheme } from '../../../src/theme/useTheme'
import { usePrivacyAccountDeletionFlow } from '../../../src/privacy/hooks/usePrivacyAccountDeletionFlow'
import { computeMenuSheetLayout } from '../../../src/layout/helpers/menuSheetLayout.helper'
import {
  buildExpectedDeletePhrase,
  normalizeDeletePhraseInput,
} from '../../../src/account/accountDeletion.constants'
import { BaseModal } from '../../ui/BaseModal'
import { getPrivacyAccountDeletionModalStyles } from './PrivacyAccountDeletionModal.styles'
import { PRIVACY_AND_DATA_SCREEN } from '../../../src/privacy/privacyAndData.constants'
import {
  PRIVACY_ACCOUNT_DELETION_MODAL,
  PRIVACY_ACCOUNT_DELETION_MODAL_CONTENT_TEST_ID,
  PRIVACY_ACCOUNT_DELETION_MODAL_TEST_ID,
} from './privacyAccountDeletionModal.constants'

export interface PrivacyAccountDeletionModalProps {
  isVisible: boolean
  onDismiss: () => void
  testID?: string
}

export const PrivacyAccountDeletionModal: React.FC<PrivacyAccountDeletionModalProps> = ({
  isVisible,
  onDismiss,
  testID,
}) => {
  const theme = useTheme()
  const { width: winW, height: winH } = useWindowDimensions()
  const {
    isReady,
    deletionAlreadyQueued,
    isDeleting,
    username,
    errorMessage,
    startDeletion,
    cancelDeletion,
    confirmDeletion,
  } = usePrivacyAccountDeletionFlow()

  const [isConfirming, setIsConfirming] = useState(false)
  const [deleteInput, setDeleteInput] = useState('')

  const layout = useMemo(() => computeMenuSheetLayout(winW, winH), [winW, winH])

  const styles = useMemo(
    () => getPrivacyAccountDeletionModalStyles(theme, layout),
    [theme, layout]
  )

  const showWebHover = Platform.OS === 'web'
  const modalTestId = testID ?? PRIVACY_ACCOUNT_DELETION_MODAL_TEST_ID

  const expectedPhrase = username ? buildExpectedDeletePhrase(username) : ''
  const canConfirm =
    !!username &&
    normalizeDeletePhraseInput(deleteInput) === expectedPhrase &&
    !isDeleting

  // Reset the inline confirm step whenever the sheet closes.
  useEffect(() => {
    if (!isVisible) {
      setIsConfirming(false)
      setDeleteInput('')
    }
  }, [isVisible])

  const handleStartPress = useCallback(() => {
    void (async () => {
      const proceed = await startDeletion()
      if (proceed) {
        setDeleteInput('')
        setIsConfirming(true)
      }
    })()
  }, [startDeletion])

  const handleCancelPress = useCallback(() => {
    cancelDeletion()
    setIsConfirming(false)
    setDeleteInput('')
  }, [cancelDeletion])

  const handleConfirmPress = useCallback(() => {
    void confirmDeletion()
  }, [confirmDeletion])

  if (!isVisible) {
    return null
  }

  return (
    <BaseModal
      isVisible={isVisible}
      onDismiss={onDismiss}
      blurIntensity={PRIVACY_ACCOUNT_DELETION_MODAL.BASE_MODAL_BLUR_INTENSITY}
      contentAlignment="center"
      contentContainerStyle={styles.modalOuter}
      testID={modalTestId}
      contentTestID={testID ? `${testID}-content` : PRIVACY_ACCOUNT_DELETION_MODAL_CONTENT_TEST_ID}
    >
      <View style={styles.sheet} pointerEvents="box-none">
        <Pressable
          style={({ pressed, hovered }) => [
            styles.closeButton,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.closeButtonPressed,
            hovered && showWebHover && styles.closeButtonHover,
          ]}
          onPress={onDismiss}
          accessibilityRole="button"
          accessibilityLabel="Close"
          hitSlop={12}
        >
          <X size={22} color={theme.gray[500]} />
        </Pressable>

        {!isReady ? (
          <View style={styles.loadingBox}>
            <ActivityIndicator size="large" color={theme.primary} />
          </View>
        ) : (
          <ScrollView
            style={styles.scroll}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={Platform.OS !== 'web'}
            keyboardShouldPersistTaps="handled"
            bounces
          >
            <Text style={styles.title} accessibilityRole="header">
              {PRIVACY_AND_DATA_SCREEN.DANGER_CARD_TITLE}
            </Text>
            <Text style={styles.copy}>{PRIVACY_AND_DATA_SCREEN.DANGER_COPY}</Text>

            {deletionAlreadyQueued ? (
              <View style={styles.statusBadge} accessibilityRole="text">
                <Clock size={16} color={theme.gray[700]} />
                <Text style={styles.statusBadgeText}>
                  {PRIVACY_AND_DATA_SCREEN.DELETION_ALREADY_QUEUED}
                </Text>
              </View>
            ) : isConfirming ? (
              <>
                <Text style={styles.confirmHint}>
                  {PRIVACY_AND_DATA_SCREEN.CONFIRM_INSTRUCTION}
                  {'\n'}
                  <Text style={styles.confirmPhrase}>{expectedPhrase}</Text>
                </Text>
                <TextInput
                  value={deleteInput}
                  onChangeText={setDeleteInput}
                  editable={!isDeleting}
                  style={styles.input}
                  autoCapitalize="none"
                  autoCorrect={false}
                  autoComplete="off"
                  textContentType="none"
                  placeholder={expectedPhrase}
                  placeholderTextColor={theme.gray[500]}
                  accessibilityLabel="Type phrase to confirm account deletion"
                  testID="privacy-account-deletion-input"
                />
                {errorMessage ? (
                  <Text style={styles.errorText} accessibilityRole="alert">
                    {errorMessage}
                  </Text>
                ) : null}
                <View style={styles.buttonRow}>
                  <Pressable
                    disabled={isDeleting}
                    onPress={handleCancelPress}
                    style={({ pressed, hovered }) => [
                      styles.cancelButton,
                      Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                      pressed && styles.cancelButtonPressed,
                      hovered && showWebHover && styles.cancelButtonHover,
                    ]}
                    accessibilityRole="button"
                    accessibilityLabel={PRIVACY_AND_DATA_SCREEN.CANCEL}
                    testID="privacy-account-deletion-cancel"
                  >
                    <Text style={styles.cancelButtonText}>
                      {PRIVACY_AND_DATA_SCREEN.CANCEL}
                    </Text>
                  </Pressable>
                  <Pressable
                    disabled={!canConfirm}
                    onPress={handleConfirmPress}
                    style={({ pressed, hovered }) => [
                      styles.confirmButton,
                      !canConfirm && styles.confirmButtonDisabled,
                      Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                      pressed && styles.confirmButtonPressed,
                      hovered && showWebHover && styles.confirmButtonHover,
                    ]}
                    accessibilityRole="button"
                    accessibilityLabel={PRIVACY_AND_DATA_SCREEN.DELETE_ACCOUNT}
                    testID="privacy-account-deletion-confirm"
                  >
                    {isDeleting ? (
                      <ActivityIndicator size="small" color={theme.gray[900]} />
                    ) : (
                      <Text style={styles.confirmButtonText}>
                        {PRIVACY_AND_DATA_SCREEN.DELETE_ACCOUNT}
                      </Text>
                    )}
                  </Pressable>
                </View>
              </>
            ) : (
              <Pressable
                disabled={isDeleting}
                onPress={handleStartPress}
                style={({ pressed, hovered }) => [
                  styles.deleteButton,
                  isDeleting && styles.deleteButtonDisabled,
                  Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                  pressed && styles.deleteButtonPressed,
                  hovered && showWebHover && styles.deleteButtonHover,
                ]}
                accessibilityRole="button"
                accessibilityLabel={PRIVACY_AND_DATA_SCREEN.DELETE_ACCOUNT}
                testID="privacy-account-deletion-submit"
              >
                {isDeleting ? (
                  <ActivityIndicator size="small" color={theme.error} />
                ) : (
                  <Text style={styles.deleteButtonText}>
                    {PRIVACY_AND_DATA_SCREEN.DELETE_ACCOUNT}
                  </Text>
                )}
              </Pressable>
            )}
          </ScrollView>
        )}
      </View>
    </BaseModal>
  )
}
